/**
 * Everything the backend knows about a plan: listing them, creating one,
 * reading one whole, and the meals on it.
 *
 * One list endpoint serves both list screens - the plan list asks for the
 * whole workspace, a client's Plans tab passes that client's `public_id` and
 * gets only theirs. `status` and `search` are filters the server applies, and
 * `page` and `page_size` cut what is left into the pages the tables show, so
 * neither screen holds more rows than it is drawing.
 *
 * The options under a meal are the backend's too now — adding one, taking one
 * away, and writing the food rows and instructions on it, which go up whole
 * when the option is saved. What the browser still keeps is the plan between
 * those calls: `plansService` holds a fetched plan while the builder works on
 * it, and the table inside an option until Save sends it.
 *
 * Everything snake_case stops here - the rest of the module sees `PlanRecord`,
 * `MealSlot` and the app's own casing.
 */

import { api, API_ENDPOINTS } from "../../../lib/api";
import type { PaginatedResponse } from "../../../lib/api";
import {
  createDishEntry,
  createMealOption,
  formatAmount,
  servingMacros,
  sortMealsByTime,
} from "../planMapping";
import type { GetToken } from "../../auth/types/auth.types";
import { planUnitLabel, toPlanStructure } from "../planStructure";
import type { PlanStructure } from "../planStructure";
import type {
  CreateMealDayPayload,
  CreateMealOptionPayload,
  CreateMealPlanPayload,
  DuplicateMealPlanPayload,
  CreateMealSlotPayload,
  DishEntry,
  Macros,
  MealOption,
  MealPlanDayResponse,
  MealPlanDetailResponse,
  MealPlanItemResponse,
  MealPlanMealResponse,
  MealPlanOptionResponse,
  MealPlanStatusParam,
  MealPlanSummaryResponse,
  MealPlanTotals,
  MealPlanTypeOption,
  MealPlanTypesResponse,
  MealOptionContents,
  MealOptionItemPayload,
  MealOptionItemsResponse,
  MealSlot,
  MealSlotTypeOption,
  MealSlotTypesResponse,
  PlanCounts,
  PlanCountsResponse,
  PlanRecord,
  PlanStatus,
  PlanSummary,
  PlanSummaryPage,
  SaveMealDayItemsPayload,
  SaveMealOptionItemsPayload,
  SendMealPlanPayload,
  SendMealPlanResponse,
  UpdateMealSlotPayload,
  WireDecimal,
} from "../types/plans.types";

/** The largest page the endpoint will serve. Anything above is clamped to it. */
export const MAX_PAGE_SIZE = 100;

/**
 * How long the PDF call is given. Rendering a document is slower than reading
 * a record back, and the default timeout is written for the latter.
 */
const PDF_TIMEOUT_MS = 60_000;

/**
 * What a list call asks for unless it says otherwise. Ten, matching the ten
 * rows both plan tables show, so a page of the API is a page of the table.
 */
export const DEFAULT_PAGE_SIZE = 10;

/** The backend's word for a plan's state, in the app's own vocabulary. */
export const toPlanStatus = (status: MealPlanStatusParam): PlanStatus =>
  status === "DRAFT" ? "draft" : "sent";

/** And back again, for the `status` filter. `null` is both - the All tab. */
export const toStatusParam = (
  status: PlanStatus | "all"
): MealPlanStatusParam | undefined => {
  if (status === "draft") return "DRAFT";
  if (status === "sent") return "ACTIVE";
  return undefined;
};

/**
 * The backend's `totals` block in the four names the screens read, or `null`
 * where it sent none.
 *
 * Whole numbers, because that is how every macro on the app is written — see
 * `formatMacro`. The one that is not a straight rename is protein: the block
 * carries `protein_g`, which is the backend's own figure for the day, and it
 * is taken as sent rather than recomputed from the rows.
 *
 * `null` rather than four zeros for a missing block, so a plan the backend has
 * no answer for is telling a screen to work it out itself rather than telling
 * it the day is empty.
 */
const toMacros = (totals: MealPlanTotals | null | undefined): Macros | null =>
  totals
    ? {
        calories: Math.round(totals.kcal ?? 0),
        carbs: Math.round(totals.carbs_g ?? 0),
        protein: Math.round(totals.protein_g ?? 0),
        fat: Math.round(totals.fat_g ?? 0),
      }
    : null;

/**
 * One row, in the shape the tables read.
 *
 * Every field is defended: a plan with no client, no type or no totals is a
 * row the list still has to render, and a dash in a cell is a better answer
 * than a screen that fails to draw. If the backend's field names turn out to
 * differ from these, this function is the only thing that has to change.
 */
const toSummary = (response: MealPlanSummaryResponse): PlanSummary => ({
  id: response.public_id,
  name: response.name ?? "",
  clientId: response.client?.public_id ?? "",
  clientName: response.client?.name ?? "",
  planType: response.plan_type ?? "",
  structure: toPlanStructure(response.structure),
  createdOn: response.created_at,
  status: toPlanStatus(response.status),
  // A list row has no meals to add up, so a plan with no totals is a row of
  // zeros rather than one with nothing in its four columns.
  macros: toMacros(response.totals) ?? {
    calories: 0,
    carbs: 0,
    protein: 0,
    fat: 0,
  },
});

/**
 * The kinds of plan the create dialog picks from — Regular, Detox, Fasting, …
 *
 * Its own call rather than part of `catalogService`: that one is the client
 * form's, every list in it answers with a bare array, and this one wraps its
 * rows in a key naming them the way the service catalogs do.
 */
export async function fetchMealPlanTypes(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<MealPlanTypeOption[]> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot load plan types.");
  }

  const response = await api.get<MealPlanTypesResponse>(
    API_ENDPOINTS.catalog.mealPlanTypes,
    { token, signal }
  );

  // Rows without a usable name are dropped rather than shown: an empty option
  // is a dead line in the list, and one the dialog could never validate.
  return (response?.plan_types ?? [])
    .filter((row) => typeof row?.name === "string" && row.name.trim())
    .map((row) => ({ id: row.id, name: row.name.trim() }));
}

/**
 * A decimal off the wire, whichever way it arrived — `2.0`, `"2.00"`, or
 * nothing at all. Anything unreadable counts as zero, the same as absent.
 */
const toNumber = (value: WireDecimal | null | undefined): number => {
  const parsed = typeof value === "string" ? Number(value) : value;
  return typeof parsed === "number" && Number.isFinite(parsed) ? parsed : 0;
};

/**
 * One food row, as the builder holds it.
 *
 * The macros are the same four the row shows anywhere else — `total_` for
 * calories and fat, so the cooking fat is in, and the adjusted protein. That
 * is the same choice `fetchFoodNutrition` makes, which is what keeps a row
 * reading the same before and after it is saved.
 *
 * The unit is written out the way the nutrition endpoint writes it, from what
 * this response carries instead: `base_units` is the quantity in grams, so
 * one unit is that over the quantity — 400 g for 2 cups is "Cup (medium) -
 * 200gm". Guarded against a zero quantity, which would divide by nothing.
 *
 * A row with no `food_id` is one that was written out by hand: nothing was
 * derived for it, so there is no adjusted protein — only the figures that were
 * typed, which come back as the gross ones. It is marked `custom` so the
 * builder opens it back in the dialog it was written in rather than as a
 * catalog row that has lost its ids.
 *
 * `nutrition` is what the whole row is worth either way — a hand-written row
 * went up priced per container, but the backend prices it the same way it
 * prices a catalog one for the quantity given, and sends back the total.
 *
 * What one container came to comes back beside it now, on
 * `container_nutrition`, and that is what the custom dish dialog reopens on.
 * Where it is missing — an older row, or a backend that did not send it — the
 * figure is divided back out of the total by `servingMacros` when the dialog
 * opens, which is what this module did for every row before the field existed.
 * The first-hand figure is the better one: dividing a rounded total by 1.5
 * containers does not land back on what was typed.
 */
const toDishEntry = (item: MealPlanItemResponse): DishEntry => {
  const quantity = toNumber(item.quantity);
  const baseUnits = toNumber(item.base_units);
  const nutrition = item.nutrition;
  const custom = item.food_id == null;

  // Rounded to whole units, the way every screen shows a macro — a plan read
  // back is judged against the same figures it will be judged against on
  // every screen after this one.
  const macros: Macros = {
    calories: Math.round(toNumber(nutrition?.total_kcal)),
    carbs: Math.round(toNumber(nutrition?.carbs_g)),
    // The protein a hand-written row was given is its gross figure — there
    // is nothing behind it to adjust, so the backend sends none.
    protein: Math.round(
      toNumber(nutrition?.protein_adjusted_g ?? nutrition?.protein_gross_g)
    ),
    fat: Math.round(toNumber(nutrition?.total_fat_g)),
  };

  return {
    id: String(item.id),
    dish: item.food_name ?? "",
    foodId: item.food_id ?? null,
    unit: item.unit_name ?? "",
    unitId: item.unit_id ?? null,
    unitLabel:
      quantity && baseUnits
        ? `${item.unit_name} - ${formatAmount(baseUnits / quantity)}${item.base_unit_name}`
        : item.unit_name ?? "",
    quantity,
    ...(custom
      ? {
          custom: true,
          // What one container comes to, and what that is counted in — the
          // two halves of the volume the dialog asked for. The backend's own
          // `container_quantity` where it sent one, and the row's grams over
          // its quantity where it did not: a row kept no conversion for at all
          // reopens with the box empty rather than with a figure invented here.
          unitGrams:
            item.container_quantity != null
              ? toNumber(item.container_quantity)
              : quantity && baseUnits
              ? baseUnits / quantity
              : null,
          unitMeasure: item.base_unit_name || null,
          // The four figures for one container, as they were typed. Absent
          // rather than null where the backend sent none, so `servingMacros`
          // goes on dividing them out of the total the way it always has.
          ...(item.container_nutrition
            ? {
                unitMacros: {
                  calories: Math.round(
                    toNumber(item.container_nutrition.kcal)
                  ),
                  carbs: Math.round(toNumber(item.container_nutrition.carbs_g)),
                  protein: Math.round(
                    toNumber(item.container_nutrition.protein_g)
                  ),
                  fat: Math.round(toNumber(item.container_nutrition.fat_g)),
                },
              }
            : {}),
        }
      : {}),
    macros,
  };
};

/** Rows in the order the backend gave them, `sort_order` first. */
const bySortOrder = <T extends { sort_order: number }>(rows: T[]): T[] =>
  [...rows].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

/**
 * An option's instructions as the builder holds them: absent where there are
 * none, which is what the backend's "" means — see `MealOption.instructions`.
 */
const toInstructions = (
  value: string | null | undefined
): string | undefined => value?.trim() || undefined;

const toMealOption = (option: MealPlanOptionResponse): MealOption => ({
  id: String(option.id),
  // Never empty: an option with no rows would be a table with nothing to
  // type into, and the builder always keeps one row to write in.
  dishes:
    option.items?.length > 0
      ? bySortOrder(option.items).map(toDishEntry)
      : [createDishEntry()],
  instructions: toInstructions(option.instructions),
});

/**
 * One day of a day-based plan, read into the same `MealOption` an option is.
 *
 * The two are the same thing on screen — a table of dishes with instructions
 * under it — so the builder holds one list either way and the plan's
 * `structure` is what decides whether it is headed Option 1 or Day 1. The day
 * number rides along on `day`: a day carries no `sort_order`, and its number
 * is both its place and its name.
 */
const toMealDay = (day: MealPlanDayResponse): MealOption => ({
  id: String(day.id),
  day: day.day,
  dishes:
    day.items?.length > 0
      ? bySortOrder(day.items).map(toDishEntry)
      : [createDishEntry()],
  instructions: toInstructions(day.instructions),
});

/** Days in the order they are eaten, which is the only order they have. */
const byDay = (days: MealPlanDayResponse[]): MealPlanDayResponse[] =>
  [...days].sort((a, b) => (a.day ?? 0) - (b.day ?? 0));

/**
 * One meal. `time_of_day` is `HH:MM:SS` and a meal keeps `HH:MM`, which is
 * also what `formatMealTime` reads.
 *
 * A meal with neither list of its own gets the one blank option the builder
 * writes into, marked as the browser's — the backend has not been told about
 * it, and it is created the moment something needs it to be. The meal itself
 * is the backend's either way, so an option can be hung off it.
 *
 * The meals themselves are ordered by that time rather than by `sort_order`
 * — see `fetchMealPlan`.
 */
const toMealSlot = (meal: MealPlanMealResponse): MealSlot => {
  // Whichever list the meal came with. A day-based plan's meals carry `days`
  // and an option-based plan's carry `options`, never both — see
  // `MealPlanMealResponse` — so the one that is there is the one to read.
  const entries =
    meal.days && meal.days.length > 0
      ? byDay(meal.days).map(toMealDay)
      : meal.options && meal.options.length > 0
      ? bySortOrder(meal.options).map(toMealOption)
      : [createMealOption()];

  return {
    id: String(meal.id),
    name: meal.label ?? "",
    time: (meal.time_of_day ?? "").slice(0, 5),
    options: entries,
  };
};

/**
 * A whole plan, as the builder edits one.
 *
 * Every meal on it is the backend's, including the three a new plan is
 * created with — the create endpoint puts Breakfast, Lunch and Dinner on it
 * and answers with them, ids and all. So a plan that comes back with none has
 * genuinely had them all removed, and opens on an empty day with Add meal
 * rather than on three the browser invented: a meal the backend has no id for
 * is one nothing can be hung off.
 *
 * The meals are put in the order of the day, which is the order every screen
 * reads them in: `sort_order` and `time_of_day` can disagree (the sample
 * response has an 08:00 meal ordered before a 07:00 one), and a day that
 * runs backwards reads as a bug. Options and the rows inside them do follow
 * `sort_order`, since nothing else orders those.
 */
const toPlanRecord = (
  response: MealPlanDetailResponse,
  fallbackId?: string
): PlanRecord => ({
  id: response.public_id ?? fallbackId ?? "",
  code: response.plan_code ?? "",
  clientId: response.client?.public_id ?? "",
  clientName: response.client?.name ?? "",
  planType: response.plan_type ?? "",
  structure: toPlanStructure(response.structure),
  name: response.name ?? "",
  createdOn: response.created_at ?? new Date().toISOString(),
  status: toPlanStatus(response.status),
  meals: sortMealsByTime((response.meals ?? []).map(toMealSlot)),
  macros: toMacros(response.totals),
});

/**
 * One plan, whole — the only place a plan's meals come from.
 *
 * A `public_id` that is not the caller's, or not a plan at all, comes back
 * 404, which reaches the caller as an `ApiError` carrying the status. That is
 * what lets `usePlan` tell a missing plan from a failed request.
 */
export async function fetchMealPlan(
  getToken: GetToken,
  publicId: string,
  signal?: AbortSignal
): Promise<PlanRecord> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot load a plan.");
  }

  const response = await api.get<MealPlanDetailResponse>(
    API_ENDPOINTS.mealPlans.detail(publicId),
    { token, signal }
  );

  return toPlanRecord(response, publicId);
}

/**
 * The plan's totals alone, read back off the backend.
 *
 * Saving an option answers with that option's rows and nothing else, so the
 * day's figures are a call behind the moment anything is written. The builder
 * asks for them again rather than adding the rows up itself, which is what
 * keeps the number across the top of the page the backend's number.
 *
 * The whole plan comes back to get them — the detail endpoint is the only one
 * that carries a plan's totals — but only the totals are read off it. The
 * meals on the response are deliberately dropped: the builder is holding the
 * day as it stands on screen, including whatever is half-written into another
 * option, and none of that is the backend's to replace.
 */
export async function fetchMealPlanTotals(
  getToken: GetToken,
  publicId: string,
  signal?: AbortSignal
): Promise<Macros | null> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot load a plan's totals.");
  }

  const response = await api.get<MealPlanDetailResponse>(
    API_ENDPOINTS.mealPlans.detail(publicId),
    { token, signal }
  );

  return toMacros(response?.totals);
}

/**
 * Sends a plan to the client, and returns it as it now stands.
 *
 * The response is a whole plan — the same shape the detail endpoint sends —
 * so it goes through the same mapping and can be written straight back over
 * the copy the browser holds. What has changed on it is the status, and the
 * `activated_at` the backend stamped it with.
 */
export async function activateMealPlan(
  getToken: GetToken,
  publicId: string,
  signal?: AbortSignal
): Promise<PlanRecord> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot activate a plan.");
  }

  const response = await api.post<MealPlanDetailResponse>(
    API_ENDPOINTS.mealPlans.activate(publicId),
    undefined,
    { token, signal }
  );

  return toPlanRecord(response, publicId);
}

/**
 * The plan as a PDF, rendered by the backend.
 *
 * The bytes rather than a link to them, so the caller hands the file straight
 * to the browser — see `savePlanPdf`. The backend renders it because it is the
 * one that knows the dietitian's brand, and because the document a client is
 * emailed and the one their dietitian downloads should be the same document.
 *
 * A longer timeout than the rest of the module: rendering a day of meals is
 * work, and the default is written for calls that only read a row back.
 */
export async function fetchMealPlanPdf(
  getToken: GetToken,
  publicId: string,
  signal?: AbortSignal
): Promise<Blob> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot download a plan.");
  }

  return api.getBlob(API_ENDPOINTS.mealPlans.pdf(publicId), {
    token,
    signal,
    // A PDF for preference, and anything rather than nothing.
    //
    // `application/pdf` alone is refused: DRF answers a bare `Accept` it has
    // no renderer for with 406 "Could not satisfy the request Accept header",
    // and this view writes the file itself rather than registering a renderer
    // to be negotiated for. The `*/*` is what gives that negotiation
    // something to land on; the bytes that come back are the document either
    // way, since it is the view and not a renderer producing them.
    headers: { Accept: "application/pdf, */*" },
    timeoutMs: PDF_TIMEOUT_MS,
  });
}

/**
 * Emails the plan to the addresses given, and answers with the ones that were
 * accepted.
 *
 * Every address goes up in one call, which is how the endpoint takes them —
 * `{ emails: [...] }`. Blank entries and repeats are dropped on the way: the
 * dialog collects addresses one at a time and there is no sense asking the
 * backend to send the same plan to the same inbox twice.
 *
 * What comes back is read for the addresses alone. If it carries none, the
 * ones that went up stand — the call succeeded, so they were sent to.
 */
export async function sendMealPlan(
  getToken: GetToken,
  publicId: string,
  emails: string[],
  signal?: AbortSignal
): Promise<string[]> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot send a plan.");
  }

  const payload: SendMealPlanPayload = {
    emails: Array.from(
      new Set(emails.map((email) => email.trim()).filter(Boolean))
    ),
  };

  const response = await api.post<SendMealPlanResponse>(
    API_ENDPOINTS.mealPlans.send(publicId),
    payload,
    { token, signal }
  );

  const accepted = response?.emails;
  return Array.isArray(accepted) && accepted.length > 0
    ? accepted
    : payload.emails;
}

/**
 * Renames a plan, and answers with what it is called now.
 *
 * Only the name is read back rather than the whole record it comes in,
 * deliberately: the response is the plan as the backend holds it, and the
 * builder is sitting on meals and rows it has not been told about yet.
 * Reading the rest of it back would throw those away — a plan answered with
 * `meals: []` opens on the usual three, and the day someone had just written
 * would be gone.
 *
 * A name the backend trimmed or changed is the one that is kept; if it sends
 * none back, the one that went up stands.
 */
export async function renameMealPlan(
  getToken: GetToken,
  publicId: string,
  name: string,
  signal?: AbortSignal
): Promise<string> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot rename a plan.");
  }

  const response = await api.patch<Partial<MealPlanDetailResponse>>(
    API_ENDPOINTS.mealPlans.detail(publicId),
    { name },
    { token, signal }
  );

  return response?.name?.trim() || name;
}

/**
 * What a meal on a plan can be called — On Waking Up, Morning Tea, … — and
 * when each is usually eaten.
 *
 * The usual time comes back as `HH:MM:SS` and is kept as the `HH:MM` a
 * `MealSlot` holds, so the dialog can fill the time in from the name. "Other"
 * has none, which is left empty rather than guessed at.
 */
export async function fetchMealSlotTypes(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<MealSlotTypeOption[]> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot load meal names.");
  }

  const response = await api.get<MealSlotTypesResponse>(
    API_ENDPOINTS.catalog.mealSlotTypes,
    { token, signal }
  );

  // Rows without a usable name are dropped rather than shown: an empty option
  // is a dead line in the list, and one the dialog could never validate.
  return (response?.meal_slot_types ?? [])
    .filter((row) => typeof row?.name === "string" && row.name.trim())
    .map((row) => ({
      id: row.id,
      name: row.name.trim(),
      defaultTime: (row.default_time ?? "").slice(0, 5),
    }));
}

/**
 * Adds a meal to a plan and returns it as the builder holds one.
 *
 * The backend decides the meal's `id` and where it sits in the day's
 * `sort_order`; the browser only says what kind of meal it is and when. The
 * id it was added under is kept on the record, since the response carries the
 * label but not the id behind it — see `MealSlot.slotTypeId`.
 */
export async function addMealSlot(
  getToken: GetToken,
  planId: string,
  payload: CreateMealSlotPayload,
  signal?: AbortSignal
): Promise<MealSlot> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot add a meal.");
  }

  const response = await api.post<MealPlanMealResponse>(
    API_ENDPOINTS.mealPlans.meals(planId),
    payload,
    { token, signal }
  );

  return { ...toMealSlot(response), slotTypeId: payload.meal_slot_type };
}

/**
 * Renames a meal, moves it in the day, or both — whichever of the two fields
 * is passed. Answers with the whole meal, in the same shape adding one does.
 */
export async function updateMealSlot(
  getToken: GetToken,
  planId: string,
  slotId: string,
  payload: UpdateMealSlotPayload,
  signal?: AbortSignal
): Promise<MealSlot> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot update a meal.");
  }

  const response = await api.patch<MealPlanMealResponse>(
    API_ENDPOINTS.mealPlans.meal(planId, slotId),
    payload,
    { token, signal }
  );

  return {
    ...toMealSlot(response),
    // Only what was sent is known; a PATCH of the time alone leaves the type
    // as it was, which the response does not name an id for.
    slotTypeId: payload.meal_slot_type,
  };
}

/**
 * Adds an option to a meal and returns it as the builder holds one.
 *
 * This is where an option first becomes the backend's. The builder always
 * shows one blank option under a meal — a meal comes back carrying none — and
 * that one is the browser's until something needs it to be real, so the first
 * call under a meal is usually for `Option 1` itself. What goes up is only
 * where the option sits: its heading and its place among the meal's others.
 *
 * The response carries no items, which `toMealOption` opens on one blank row
 * for the same reason a meal opens on one option. The rows already typed into
 * a local option are the caller's to keep — see `createMissingOptions`.
 */
export async function addMealOption(
  getToken: GetToken,
  planId: string,
  slotId: string,
  payload: CreateMealOptionPayload,
  signal?: AbortSignal
): Promise<MealOption> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot add an option.");
  }

  const response = await api.post<MealPlanOptionResponse>(
    API_ENDPOINTS.mealPlans.options(planId, slotId),
    payload,
    { token, signal }
  );

  return toMealOption(response);
}

/**
 * A row as the items endpoint takes one, or `null` for one it cannot take.
 *
 * Two shapes go up, and the row decides which: one picked off the catalog is
 * sent as its two ids and priced by the backend, and one written out by hand
 * is sent as words, the container it is served in, and what one of that
 * container is worth — see `CatalogItemPayload` and `CustomItemPayload`.
 *
 * What cannot be sent either way is a row still being written: no food, no
 * unit, or nothing of it to eat. That is dropped for the same reason
 * `dishMacros` counts it as nothing — it is a row someone is in the middle of,
 * not food on a plan. So is a row left over from before the catalog endpoints
 * landed, which names its food but is not marked as one written by hand: it
 * has no ids to be priced from and no figures of its own, so there is nothing
 * to say about it.
 *
 * On a catalog row the name goes up beside the ids. It is not what identifies
 * the food — `food_id` is — so it is sent as what the row says on screen, and
 * left off entirely rather than sent blank by a row that has yet to be named.
 * A row picked off the catalog is named by the picking, so in practice this is
 * only absent where a name was cleared out from under an otherwise complete
 * row.
 */
export const toItemPayload = (
  entry: DishEntry
): MealOptionItemPayload | null => {
  const name = entry.dish.trim();
  const unit = entry.unit.trim();

  if (entry.custom) {
    // Everything about the row travels with it: nothing on the backend can
    // work any of it out from a food the catalog has never heard of.
    if (!name || !unit || entry.quantity <= 0) return null;

    // Sent for one container rather than for the whole row, which is what the
    // dialog asked for and what the backend stores — it multiplies by the
    // quantity itself, the way it prices a catalog row for the quantity given.
    const container = servingMacros(entry);

    return {
      food_name: name,
      unit_name: unit,
      // The measure the container was given in, and what one of it comes to.
      // Grams for a row that never named one — the dialog's own default, and
      // what nearly everything is weighed in.
      base_unit_name: entry.unitMeasure || "gm",
      container_quantity: (entry.unitGrams ?? 0).toFixed(2),
      quantity: entry.quantity.toFixed(2),
      container_kcal: container.calories.toFixed(2),
      container_protein_g: container.protein.toFixed(2),
      container_carbs_g: container.carbs.toFixed(2),
      container_fat_g: container.fat.toFixed(2),
    };
  }

  if (entry.foodId == null || entry.unitId == null || entry.quantity <= 0) {
    return null;
  }

  return {
    food_id: entry.foodId,
    unit_id: entry.unitId,
    // A decimal string, which is how the backend keeps it — the same way a
    // service's price travels.
    quantity: entry.quantity.toFixed(2),
    ...(name ? { food_name: name } : {}),
  };
};

/**
 * Writes an option's food rows and instructions, and returns them as the
 * builder holds them.
 *
 * The option's whole table goes up at once, so it ends up holding exactly
 * what was on screen — a row deleted from it is a row off the option, with no
 * separate call to make. The rows the backend has no way to store are dropped
 * on the way rather than by the caller: the wire's shape is this module's to
 * know, and a screen handing over the rows it holds is the whole of what it
 * should have to say. The instructions go up beside the rows, trimmed, and
 * as "" where the box was empty or closed — the backend keeps a string
 * either way.
 *
 * What comes back is those rows as the backend stored and priced them, which
 * is what replaces them on the page: the ids are its own afterwards, and the
 * macros are the figures the plan will read back with — the ones it worked out
 * for a catalog row, and the ones that were typed for a row written by hand.
 * An option stored with nothing on it opens on one blank row, the same as one
 * read off a plan. Instructions the response does not echo are kept as they
 * were sent, the way a rename keeps its name — a backend that has yet to
 * learn the field should not read as one that cleared it.
 */
export async function saveMealOptionItems(
  getToken: GetToken,
  planId: string,
  slotId: string,
  optionId: string,
  option: MealOptionContents,
  signal?: AbortSignal
): Promise<MealOptionContents> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot save an option.");
  }

  const instructions = option.instructions?.trim() ?? "";
  const payload: SaveMealOptionItemsPayload = {
    instructions,
    items: option.dishes
      .map(toItemPayload)
      .filter((item): item is MealOptionItemPayload => item !== null),
  };

  const response = await api.put<MealOptionItemsResponse>(
    API_ENDPOINTS.mealPlans.optionItems(planId, slotId, optionId),
    payload,
    { token, signal }
  );

  const items = response?.items ?? [];
  return {
    dishes:
      items.length > 0 ? bySortOrder(items).map(toDishEntry) : [createDishEntry()],
    instructions: toInstructions(response?.instructions ?? instructions),
  };
}

/**
 * Takes an option off a meal, and with it every food row on it.
 *
 * Addressed under the plan, like the call that creates one — see
 * `API_ENDPOINTS.mealPlans.option`.
 */
export async function removeMealOption(
  getToken: GetToken,
  planId: string,
  slotId: string,
  optionId: string,
  signal?: AbortSignal
): Promise<void> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot remove an option.");
  }

  await api.delete<void>(
    API_ENDPOINTS.mealPlans.option(planId, slotId, optionId),
    { token, signal }
  );
}

/**
 * Adds a day to a meal of a day-based plan, and returns it as the builder
 * holds one.
 *
 * What an option's create is to an option-based plan, with one difference
 * worth stating: nothing about where the day sits goes up. A day is the day
 * after the last one and the backend numbers it, where an option's place among
 * a meal's alternatives is the browser's to say. So the payload is the note
 * under the table and nothing else — see `CreateMealDayPayload`.
 *
 * The response carries no items, which `toMealDay` opens on one blank row for
 * the same reason a meal opens on one entry.
 */
export async function addMealDay(
  getToken: GetToken,
  planId: string,
  slotId: string,
  payload: CreateMealDayPayload,
  signal?: AbortSignal
): Promise<MealOption> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot add a day.");
  }

  const response = await api.post<MealPlanDayResponse>(
    API_ENDPOINTS.mealPlans.days(planId, slotId),
    payload,
    { token, signal }
  );

  return toMealDay(response);
}

/**
 * Writes a day's dishes and instructions, and returns them as the builder
 * holds them.
 *
 * `saveMealOptionItems` for a day: the same verb, the same body, the same
 * replace-the-whole-table meaning, and the same rows dropped on the way out —
 * see `toItemPayload`. The one difference is the answer, which is the whole
 * day rather than the rows alone. Only the rows and the note are read off it;
 * the day's own totals are not what the builder prints, and the plan's are
 * read back separately after a save.
 */
export async function saveMealDayItems(
  getToken: GetToken,
  planId: string,
  slotId: string,
  dayId: string,
  day: MealOptionContents,
  signal?: AbortSignal
): Promise<MealOptionContents> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot save a day.");
  }

  const instructions = day.instructions?.trim() ?? "";
  const payload: SaveMealDayItemsPayload = {
    instructions,
    items: day.dishes
      .map(toItemPayload)
      .filter((item): item is MealOptionItemPayload => item !== null),
  };

  const response = await api.put<MealPlanDayResponse>(
    API_ENDPOINTS.mealPlans.dayItems(planId, slotId, dayId),
    payload,
    { token, signal }
  );

  const items = response?.items ?? [];
  return {
    dishes:
      items.length > 0 ? bySortOrder(items).map(toDishEntry) : [createDishEntry()],
    instructions: toInstructions(response?.instructions ?? instructions),
  };
}

/** Takes a day off a meal, and with it every dish on it. */
export async function removeMealDay(
  getToken: GetToken,
  planId: string,
  slotId: string,
  dayId: string,
  signal?: AbortSignal
): Promise<void> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot remove a day.");
  }

  await api.delete<void>(API_ENDPOINTS.mealPlans.day(planId, slotId, dayId), {
    token,
    signal,
  });
}

/* ── One meal's entries, whichever they are ─────────────────────────────────
 * An option and a day are the same thing on screen — a table of dishes with a
 * note under it — and the builder holds one list for both. What differs is the
 * path they are written to and, for a create, what the backend needs telling.
 * These three are where that difference lives, so no screen has to carry it.
 */

/**
 * Adds an entry to a meal — an option, or a day.
 *
 * `index` is where it sits, zero-based, and is only used by an option: it is
 * what the option is called and where it goes among the meal's alternatives. A
 * day is the day after the last one and is numbered by the backend, so its
 * create sends the note and nothing else.
 */
export async function addMealEntry(
  getToken: GetToken,
  structure: PlanStructure,
  planId: string,
  slotId: string,
  index: number,
  entry?: Pick<MealOption, "instructions">,
  signal?: AbortSignal
): Promise<MealOption> {
  if (structure === "day") {
    return addMealDay(
      getToken,
      planId,
      slotId,
      { instructions: entry?.instructions?.trim() ?? "" },
      signal
    );
  }

  return addMealOption(
    getToken,
    planId,
    slotId,
    {
      name: planUnitLabel(structure, index + 1),
      sort_order: index + 1,
    },
    signal
  );
}

/** Writes an entry's dishes and instructions, whichever kind it is. */
export async function saveMealEntryItems(
  getToken: GetToken,
  structure: PlanStructure,
  planId: string,
  slotId: string,
  entryId: string,
  entry: MealOptionContents,
  signal?: AbortSignal
): Promise<MealOptionContents> {
  return structure === "day"
    ? saveMealDayItems(getToken, planId, slotId, entryId, entry, signal)
    : saveMealOptionItems(getToken, planId, slotId, entryId, entry, signal);
}

/** Takes an entry off a meal, and with it every dish on it. */
export async function removeMealEntry(
  getToken: GetToken,
  structure: PlanStructure,
  planId: string,
  slotId: string,
  entryId: string,
  signal?: AbortSignal
): Promise<void> {
  return structure === "day"
    ? removeMealDay(getToken, planId, slotId, entryId, signal)
    : removeMealOption(getToken, planId, slotId, entryId, signal);
}

/** Takes a meal off a plan, and with it every option and row underneath. */
export async function removeMealSlot(
  getToken: GetToken,
  planId: string,
  slotId: string,
  signal?: AbortSignal
): Promise<void> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot remove a meal.");
  }

  await api.delete<void>(API_ENDPOINTS.mealPlans.meal(planId, slotId), {
    token,
    signal,
  });
}

/**
 * Creates a plan and returns it in the shape the builder edits.
 *
 * The backend decides everything about it: the `public_id` the plan is opened
 * by, the `plan_code` it is numbered with, that it starts as a draft, and the
 * three meals it starts with — Breakfast, Lunch and Dinner, each at the hour
 * its slot type is usually eaten at. The browser adds nothing.
 *
 * `structure` is the one thing the create decides that the plan cannot be
 * talked out of afterwards, and it decides the shape of what comes back: a
 * day-based plan's three meals each open with one day on them, where an
 * option-based plan's open with no options at all.
 */
export async function createMealPlan(
  getToken: GetToken,
  payload: CreateMealPlanPayload,
  signal?: AbortSignal
): Promise<PlanRecord> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot create a plan.");
  }

  const response = await api.post<MealPlanDetailResponse>(
    API_ENDPOINTS.mealPlans.list,
    payload,
    { token, signal }
  );

  // The same mapping the detail endpoint goes through — a create answers with
  // the same shape, the three meals it opened the plan with included.
  const record = toPlanRecord(response);
  return {
    ...record,
    clientId: record.clientId || payload.client,
    name: record.name || payload.name,
    // What was asked for, where the response did not say. The structure is
    // what the whole builder is drawn from, and a plan created as day-based
    // that opened as option-based would be the wrong screen entirely.
    structure: response?.structure
      ? record.structure
      : toPlanStructure(payload.structure),
  };
}

/**
 * Copies a plan, and returns the copy in the shape the builder edits.
 *
 * The whole day comes back on the response — every meal, every option and
 * every food row under them, each with an `id` of its own — so the copy is a
 * plan the builder can work on straight away, without reading it back. It is
 * mapped through `toPlanRecord` for that reason: what a duplicate answers
 * with is the detail shape, not a list row.
 *
 * The copy is always a draft, however the plan it came from stood: a plan the
 * client has already been sent is not something to be edited, and a copy is
 * made in order to edit it. The client is the source plan's, which is why the
 * payload does not carry one — see `DuplicateMealPlanPayload`.
 */
export async function duplicateMealPlan(
  getToken: GetToken,
  publicId: string,
  payload: DuplicateMealPlanPayload,
  signal?: AbortSignal
): Promise<PlanRecord> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot duplicate a plan.");
  }

  const response = await api.post<MealPlanDetailResponse>(
    API_ENDPOINTS.mealPlans.duplicate(publicId),
    payload,
    { token, signal }
  );

  // The name that went up stands if the backend sent none back, the same way
  // a rename keeps the one it asked for.
  const record = toPlanRecord(response);
  return { ...record, name: record.name || payload.name };
}

/**
 * How many plans of each kind the workspace holds — the three totals across
 * the top of the Create plan screen, in one call.
 *
 * Every kind at once, from an endpoint that belongs to none of them, which is
 * why it is read here beside the screen that draws it rather than by each
 * kind's own service. A figure the backend left out reads as nought rather
 * than as a hole in the row.
 */
export async function fetchPlanCounts(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<PlanCounts> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot count plans.");
  }

  const response = await api.get<PlanCountsResponse>(
    API_ENDPOINTS.planCounts,
    { token, signal }
  );

  return {
    meal: response?.total_meal_plans ?? 0,
    supplement: response?.total_supplement_plans ?? 0,
    exercise: response?.total_exercise_plans ?? 0,
  };
}

export interface FetchMealPlansOptions {
  /**
   * A client's `public_id`. Left off, the call asks for every plan in the
   * workspace - which is what the plan list wants and the client's tab does
   * not.
   */
  client?: string;
  /** `"all"` leaves the filter off entirely, which is how both are asked for. */
  status?: PlanStatus | "all";
  /** Matched against the plan name by the backend. Blank is left off. */
  search?: string;
  /** 1-based, the way the endpoint counts. Left off asks for the first. */
  page?: number;
  /**
   * Defaults to `DEFAULT_PAGE_SIZE`, and is capped at `MAX_PAGE_SIZE` - all
   * the backend will serve.
   */
  pageSize?: number;
  signal?: AbortSignal;
}

/** One page of plans. */
export async function fetchMealPlans(
  getToken: GetToken,
  {
    client,
    status = "all",
    search,
    page,
    pageSize = DEFAULT_PAGE_SIZE,
    signal,
  }: FetchMealPlansOptions = {}
): Promise<PlanSummaryPage> {
  const token = await getToken();
  if (!token) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error("No active Clerk session - cannot load plans.");
  }

  const response = await api.get<PaginatedResponse<MealPlanSummaryResponse>>(
    API_ENDPOINTS.mealPlans.list,
    {
      token,
      // `undefined` entries are dropped by the API client, so an unfiltered
      // ask never puts an empty `client=` or `search=` in the URL.
      query: {
        client: client || undefined,
        status: toStatusParam(status),
        search: search?.trim() || undefined,
        page,
        page_size: Math.min(pageSize, MAX_PAGE_SIZE),
      },
      signal,
    }
  );

  return {
    count: response?.count ?? 0,
    hasNext: !!response?.next,
    plans: (response?.results ?? []).map(toSummary),
  };
}
